import { TASK_STATUSES_REVERSE_MAP } from "../TaskConstants";

export const PAGE_SIZE = 10;
export const POLL_INTERVAL_MS = 10 * 1000;
export const TASK_LIST_TOP_SENTINEL_ID = "task-list-top-sentinel";
export const AT_TOP_BAND_PX = 220;
export const OPEN_TASK_STATUSES = new Set([
	TASK_STATUSES_REVERSE_MAP.IN_PROGRESS,
	TASK_STATUSES_REVERSE_MAP.READY_TO_SEND,
	TASK_STATUSES_REVERSE_MAP.PICKED_UP,
]);

const SYNTHETIC_CURSOR_REWIND_MS = 50;

export function isOpenTask(task) {
	return OPEN_TASK_STATUSES.has(Number(task?.status));
}

export function encodeTaskListCursor(time, id) {
	return btoa(`${time}_${id}`).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function getTaskTimestamp(task) {
	const time = Number(task?.actualPickTime);
	return Number.isFinite(time) ? time : null;
}

export function getTaskCursor(task) {
	if (task?.cursor) return task.cursor;
	const time = getTaskTimestamp(task);
	if (!task?._id || time == null) return null;
	return encodeTaskListCursor(time, task._id);
}

/**
 * Poll exclusive-after the row older than the oldest still-open task so that
 * open row is included and can be patched. No older neighbor: encode the open
 * task's id at actualPickTime - 50ms. No open rows: exclusive after startCursor.
 */
export function pollAfterCursor(taskList, startCursor) {
	if (!taskList?.length) {
		return { after: startCursor };
	}

	let oldestOpenIndex = -1;
	for (let i = 0; i < taskList.length; i++) {
		if (isOpenTask(taskList[i])) oldestOpenIndex = i;
	}
	if (oldestOpenIndex === -1) {
		return { after: startCursor };
	}

	const olderNeighbor = taskList[oldestOpenIndex + 1];
	const olderNeighborCursor = getTaskCursor(olderNeighbor);
	if (olderNeighborCursor) {
		return { after: olderNeighborCursor, inclusive: true };
	}

	const oldestOpen = taskList[oldestOpenIndex];
	const time = getTaskTimestamp(oldestOpen);
	if (oldestOpen?._id && time != null) {
		return {
			after: encodeTaskListCursor(time - SYNTHETIC_CURSOR_REWIND_MS, oldestOpen._id),
			inclusive: true,
		};
	}
	return { after: startCursor };
}

export function splitPollPage(tasks, taskList, pendingNewer) {
	const listIds = new Set();
	for (const task of taskList || []) {
		if (task?._id) listIds.add(task._id);
	}
	const pendingIds = new Set();
	for (const task of pendingNewer || []) {
		if (task?._id) pendingIds.add(task._id);
	}

	const overlapping = [];
	const notOnList = [];
	const fresh = [];
	for (const task of tasks || []) {
		if (!task?._id) continue;
		if (listIds.has(task._id)) {
			overlapping.push(task);
			continue;
		}
		notOnList.push(task);
		if (!pendingIds.has(task._id)) fresh.push(task);
	}
	return { overlapping, notOnList, fresh };
}

export function isTaskListAtTop() {
	// 8px epsilon: browsers often report fractional scroll / slight overscroll, so
	// strict === 0 would flap between prepend and chip. Treat near-zero as "at top".
	if ((window.scrollY || window.pageYOffset) > 8) return false;
	const sentinelElement = document.getElementById(TASK_LIST_TOP_SENTINEL_ID);
	if (!sentinelElement) return false;
	const rect = sentinelElement.getBoundingClientRect();
	// Same epsilon for the sentinel: allow a few px above the viewport top.
	return rect.bottom > 0 && rect.top >= -8 && rect.top <= AT_TOP_BAND_PX;
}

export function scrollToRequiredItem(id) {
	const element = document.getElementById(id);
	if (!element) return;
	element.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
}

// True when a and b are the same tasks in the same order (by _id).
export function sameTaskIdSequence(a, b) {
	if (!a || !b || a.length !== b.length) return false;
	return a.every((task, i) => task?._id === b[i]?._id);
}

export function clearPending(setPendingNewer, setPendingIsGap, setPendingStartCursor) {
	setPendingNewer([]);
	setPendingIsGap(false);
	setPendingStartCursor(null);
}
